/**
 * Auth store. Holds the access token + session identity IN MEMORY ONLY.
 *
 * Security properties:
 *   • Nothing auth-related is persisted to localStorage. On reload, identity
 *     is re-derived from /api/auth/me (the httpOnly refresh cookie re-auths).
 *   • The access token never touches storage; it lives in this store only.
 *   • The refresh token is an httpOnly cookie — JS cannot read it.
 *
 * This store registers itself as the API client's token provider so the client
 * can inject + transparently refresh tokens without import cycles.
 */

import { create } from "zustand";
import { authApi } from "@/lib/api";
import { registerTokenProvider } from "@/lib/api-client";
import type { SessionUser, SessionInstance } from "@/types";

interface AuthState {
  accessToken: string | null;
  expiresAt: number | null;
  user: SessionUser | null;
  instance: SessionInstance | null;
  tenantScope: string | null;
  tenantName: string | null;
  status: "loading" | "authenticated" | "unauthenticated";
  error: string | null;

  login: (username: string, password: string, instanceName?: string) => Promise<void>;
  selectInstance: (instanceName: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  bootstrap: () => Promise<void>; // re-derive session on reload
  clear: () => void;
}

export const useAuth = create<AuthState>((set, get) => {
  // Register as the API client's token provider (single-flight refresh).
  registerTokenProvider({
    getAccessToken: () => get().accessToken,
    refresh: async () => {
      try {
        await get().refresh();
        return get().accessToken;
      } catch {
        get().clear();
        return null;
      }
    },
    clear: () => get().clear(),
  });

  return {
    accessToken: null,
    expiresAt: null,
    user: null,
    instance: null,
    tenantScope: null,
    tenantName: null,
    status: "loading",
    error: null,

    async login(username, password, instanceName) {
      set({ error: null });
      try {
        const res = await authApi.login(username, password, instanceName);
        set({
          accessToken: res.accessToken,
          expiresAt: res.expiresAt,
          user: res.user,
          instance: res.instance,
          tenantScope: res.tenantScope,
          tenantName: res.tenantName ?? null,
          status: "authenticated",
        });
      } catch (err) {
        set({ error: err instanceof Error ? err.message : "Login failed" });
        throw err;
      }
    },

    async selectInstance(instanceName) {
      const res = await authApi.selectInstance(instanceName);
      set({
        accessToken: res.accessToken,
        expiresAt: res.expiresAt,
        instance: res.instance,
        tenantScope: res.tenantScope,
        tenantName: res.tenantName ?? null,
      });
    },

    async logout() {
      try {
        await authApi.logout();
      } catch {
        /* the cookie may already be gone */
      }
      get().clear();
    },

    async refresh() {
      const res = await authApi.refresh();
      set({ accessToken: res.accessToken, expiresAt: res.expiresAt });
    },

    async bootstrap() {
      // Called once on app load. The in-memory access token is gone after a
      // reload, so refresh FIRST to get a fresh access token, then call /me.
      // This avoids the noisy transient 401 on /me that the api-client's
      // transparent-refresh would otherwise paper over (but still log).
      try {
        await get().refresh();
      } catch {
        // No valid refresh cookie → not logged in.
        get().clear();
        set({ status: "unauthenticated" });
        return;
      }
      try {
        const me = await authApi.me();
        set({
          user: me.user,
          instance: me.instance,
          tenantScope: me.tenantScope,
          tenantName: me.tenantName ?? null,
          status: "authenticated",
        });
      } catch {
        get().clear();
        set({ status: "unauthenticated" });
      }
    },

    clear() {
      set({
        accessToken: null,
        expiresAt: null,
        user: null,
        instance: null,
        tenantScope: null,
        tenantName: null,
        status: "unauthenticated",
      });
    },
  };
});

/** Convenience selectors. */
export const useIsAuthenticated = () => useAuth((s) => s.status === "authenticated");
export const useIsSuperAdmin = () => useAuth((s) => s.user?.role === "super_admin");
